import type { LinkedItemsSummary } from '@/lib/api/projects.api'
import type { ProjectFilters } from '@/store/project/types'
import { projectsApi } from '@/lib/api/projects.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import type { ProjectWithEntity } from '@scrambl/shared'
import { ModuleTypeEnum } from '@scrambl/shared'

import { queryKeys } from '../query-keys'

interface ProjectsResponse {
  data: ProjectWithEntity[]
  meta: {
    total: number
  }
}

const normalizeStringArrayFilter = (value: unknown): string[] => {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? [value]
      : []

  return Array.from(
    new Set(
      values
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.trim())
        .filter(Boolean),
    ),
  )
}

/**
 * Hook to fetch projects with filters
 * Filters are stored in Zustand, but the data comes from TanStack Query
 */
export function useProjectsQuery(
  filters?: ProjectFilters,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectsResponse>({
    queryKey: queryKeys.projects.list(filters as Record<string, unknown>),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      // Merge filters with defaults (allow override of includeCustomFields)
      const requestFilters: ProjectFilters = {
        includeCustomFields: filters?.includeCustomFields ?? true, // Default to true, but allow override
        ...filters,
      }
      const entityColumnFilterIds = normalizeStringArrayFilter(
        requestFilters.columnFilters?.entityId,
      )

      if (entityColumnFilterIds.length > 0) {
        delete requestFilters.entityId
        if (requestFilters.moduleType === ModuleTypeEnum.ENTITY) {
          delete requestFilters.moduleType
          delete requestFilters.moduleId
        }
      }

      return projectsApi.getProjects(requestFilters)
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    placeholderData: previous => previous,
    staleTime: 60 * 1000, // 1 minute - projects can change
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  })
}

/**
 * Hook to fetch a single project by ID
 */
export function useProjectQuery(
  id: string | undefined,
  options?: {
    includeCustomFields?: boolean
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectWithEntity>({
    queryKey: queryKeys.projects.detail(id || '', {
      includeCustomFields: options?.includeCustomFields !== false,
    }),
    queryFn: async () => {
      return projectsApi.getProject(id!, {
        includeCustomFields: options?.includeCustomFields !== false,
      })
    },
    enabled: options?.enabled !== false && !!id && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch projects for a specific entity
 */
/**
 * Hook to fetch linked items summary for a project (used by delete dialog)
 */
export function useProjectLinkedItemsSummaryQuery(
  projectId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  return useQuery<LinkedItemsSummary>({
    queryKey: [
      ...queryKeys.projects.detail(projectId || ''),
      'linked-items-summary',
    ],
    queryFn: () => projectsApi.getLinkedItemsSummary(projectId!),
    enabled: options?.enabled !== false && !!projectId,
    staleTime: 0, // Always refetch when dialog opens
  })
}

export function useProjectsByEntityQuery(
  entityId: string | undefined,
  filters?: {
    status?: string
    limit?: number
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectsResponse>({
    queryKey: queryKeys.projects.list({ ...filters, entityId }),
    queryFn: () =>
      projectsApi.getProjectsByEntityId(entityId!, {
        status: filters?.status,
        limit: filters?.limit,
      }),
    enabled:
      filters?.enabled !== false && !!entityId && !!currentOrganization?.id,
    placeholderData: previous => previous,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Generic polymorphic variant — fetch projects whose moduleType+moduleId point
 * at any parent (e.g. a custom collection entry, a deal, a lead). Mirrors
 * useProjectsByEntityQuery so the same caching/refetch semantics apply.
 */
export function useProjectsByModuleQuery(
  moduleType: string | undefined,
  moduleId: string | undefined,
  filters?: {
    status?: string
    limit?: number
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectsResponse>({
    queryKey: queryKeys.projects.list({ ...filters, moduleType, moduleId }),
    queryFn: () =>
      projectsApi.getProjectsByModule(moduleType!, moduleId!, {
        status: filters?.status,
        limit: filters?.limit,
      }),
    enabled:
      filters?.enabled !== false &&
      !!moduleType &&
      !!moduleId &&
      !!currentOrganization?.id,
    placeholderData: previous => previous,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * useProjectsQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const projectsListQuery = useProjectsQuery
